import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { clearContentRequested } from 'features/application/actions';
import { ClearContentTypes } from 'features/application/types';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import {
  selectBreakoutRoomsStatus,
  selectIsBreakoutRoom,
} from 'features/breakout-rooms/breakoutRoomsSlice';

const contentTypeOptions: Array<{ value: keyof ClearContentTypes; label: string }> = [
  { value: 'publicChat', label: 'settings:clear_content.content_types.chat_messages' },
  { value: 'roomLibrary', label: 'settings:clear_content.content_types.public_room_files' },
  { value: 'sharedFiles', label: 'settings:clear_content.content_types.shared_files' },
  { value: 'whiteboards', label: 'settings:clear_content.content_types.whiteboards' },
  { value: 'sharedNotes', label: 'settings:clear_content.content_types.shared_notes' },
  { value: 'qa', label: 'settings:clear_content.content_types.qa_entries' },
  { value: 'pollResults', label: 'settings:clear_content.content_types.poll_results' },
];

const ClearContentConfirmation = () => {
  const { t } = useTranslation(['modals', 'settings']);
  const dispatch = useAppDispatch();

  const modalData = useAppSelector(selectActiveModalData);
  const selectedContentTypes = modalData.selectedContentTypes as ClearContentTypes;

  const { hasPermissions } = usePermissions();

  const isBreakout = useAppSelector(selectIsBreakoutRoom);
  const breakoutRoomsStatus = useAppSelector(selectBreakoutRoomsStatus);

  const canClearContent =
    hasPermissions(PermissionTypes.clearContent) &&
    !isBreakout &&
    breakoutRoomsStatus !== 'started';

  const modalShouldBeClosed =
    !canClearContent ||
    !selectedContentTypes ||
    Object.values(selectedContentTypes || {}).every((value) => !value);

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('clearContentConfirmation'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const handleCancel = () => {
    dispatch(closeModal('clearContentConfirmation'));
  };

  const handleOk = () => {
    dispatch(clearContentRequested(selectedContentTypes));
    dispatch(closeModal('clearContentConfirmation'));
  };

  const availableOptions = contentTypeOptions.filter(
    (option) => selectedContentTypes?.[option.value]
  );

  return (
    <ConfirmationDialog
      open={!modalShouldBeClosed}
      title={t('clear_content_confirmation.title')}
      description={t('clear_content_confirmation.description')}
      cancelText={t('clear_content_confirmation.btn_cancel_label')}
      okText={t('clear_content_confirmation.btn_ok_label')}
      onCancel={handleCancel}
      onOk={handleOk}
      hideCloseButton
      okButtonProps={{ color: 'error' }}
    >
      {availableOptions.length > 0 ? (
        <Box component="ul" sx={{ mt: 2, pl: 4, mb: 0 }}>
          {availableOptions.map((option) => (
            <Typography key={option.value} component="li" variant="body2" sx={{ py: 1 }}>
              {t(option.label)}
            </Typography>
          ))}
        </Box>
      ) : null}
    </ConfirmationDialog>
  );
};

export default ClearContentConfirmation;
